import { useQuery } from '@tanstack/react-query'
import { REGIONS } from 'v2/configs/constants'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RIndividualKycStatus } from 'v2/types/api/india-kyc'
import { ComplianceApplicationStatus } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { useIndiaKycV2State } from '../india-kyc/use-india-individual-kyc'
import { useComplianceGateQuery } from '../number-compliance-v2/queries-v2'
import type { RV2ComplianceApplication } from '../number-compliance-v2/types-v2'
import { phoneNumbersQueryConfig } from './queries'

// Same params the phone-numbers list page uses for its own count check, so
// both reads share one cached query.
const FIRST_RUN_PARAMS = { paginated: false, limit: 1 }

export interface IndiaKycGateInput {
  isIndiaRegion: boolean
  hasPhoneNumbers: boolean
  /** Applications the gate query read, newest first. */
  applications: Pick<RV2ComplianceApplication, 'status'>[]
  /** The org's real total, which may exceed what was read. */
  totalCount: number
  /**
   * False while either underlying query has not actually returned data —
   * covers both a cold cache and a failed read. A failed read must not be
   * mistaken for a confirmed-empty organisation, so it gates the same as
   * loading rather than falling through to the counts below (which default
   * to empty in both cases).
   */
  dataReady: boolean
  /** The individual KYC's status, which can approve the org on its own. */
  individualKycStatus?: RIndividualKycStatus
}

export interface IndiaKycGateFlags {
  isFirstRun: boolean
  isKycRequired: boolean
}

/**
 * The gate decision, pure and separately testable — the hook around it only
 * supplies the two queries.
 *
 * `isFirstRun` (nothing at all yet) and `isKycRequired` (no approval yet) are
 * different questions and diverge for the whole time an application is in review
 * or has come back rejected. The home page's credits prompt and Monthly Usage
 * blanking want the first; the Phone Numbers page wants the second.
 *
 * Both read false outside India. They also read false whenever `dataReady` is
 * false — a cold cache or a failed fetch look identical from here (both leave
 * the counts at their empty defaults), and a failed read must not be read as
 * a confirmed-empty organisation.
 */
export const deriveIndiaKycGate = ({
  isIndiaRegion,
  hasPhoneNumbers,
  applications,
  totalCount,
  dataReady,
  individualKycStatus,
}: IndiaKycGateInput): IndiaKycGateFlags => {
  if (!dataReady) return { isFirstRun: false, isKycRequired: false }

  // Fail OPEN when the org has more applications than the gate query reads: we
  // cannot prove none of them is approved, and wrongly gating hides the whole
  // page from a customer who is entitled to buy. Under the cap the page is
  // authoritative, so an absent approval really is an absent approval.
  const canSeeEveryApplication = totalCount <= applications.length
  const hasApprovedApplication = applications.some(
    (application) => application.status === ComplianceApplicationStatus.ACCEPTED,
  )

  const hasIndividualOutcome =
    individualKycStatus === 'review' ||
    individualKycStatus === 'approved' ||
    individualKycStatus === 'failed'

  return {
    isFirstRun: isIndiaRegion && !hasPhoneNumbers && totalCount === 0 && !hasIndividualOutcome,
    isKycRequired:
      isIndiaRegion &&
      !hasPhoneNumbers &&
      canSeeEveryApplication &&
      !hasApprovedApplication &&
      individualKycStatus !== 'approved',
  }
}

// India KYC gates. Approval is required before buying a number, so this answers
// two different questions: has the org started at all (`isFirstRun`), and does it
// hold an approval yet (`isKycRequired`). They diverge for the whole time an
// application sits in review or comes back rejected.
/**
 * @param enabled Callers that only read the gate on one screen should pass a
 * predicate for it. Both queries are otherwise issued — and, while anything is
 * in review, polled — from every mount point, including tabs that discard the
 * result.
 */
export const useIndiaComplianceFirstRun = (enabled = true) => {
  const isIndiaRegion = getLocalAuth().region === REGIONS.IN
  const shouldFetch = isIndiaRegion && enabled
  const [isKycV2, kyc] = useIndiaKycV2State(shouldFetch)

  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useQuery({
    queryKey: [...getSettingsQueryKeys().phoneNumbers, FIRST_RUN_PARAMS],
    queryFn: () => phoneNumbersQueryConfig().fetchService(FIRST_RUN_PARAMS),
    enabled: shouldFetch,
  })

  const { data: complianceGate, isLoading: isLoadingCompliance } =
    useComplianceGateQuery(shouldFetch)

  const applications = complianceGate?.applications ?? []
  const flags = deriveIndiaKycGate({
    isIndiaRegion,
    hasPhoneNumbers: (phoneNumbersData?.data?.numbers?.length ?? 0) > 0,
    applications,
    totalCount: complianceGate?.totalCount ?? 0,
    // Both undefined during loading AND after an error (react-query leaves
    // `data` alone on failure) — either way, the counts above are not real.
    // So is a loading or failed individual KYC read; an unread one (no UCC
    // View) gates as business.
    dataReady:
      phoneNumbersData !== undefined &&
      complianceGate !== undefined &&
      !kyc.isLoading &&
      !kyc.isLoadingError,
    individualKycStatus: kyc.data?.status,
  })

  return {
    isLoading: shouldFetch && (isLoadingPhoneNumbers || isLoadingCompliance || kyc.isLoading),
    ...flags,
    /** Newest application, which is the one the gate screen reports on. */
    latestApplication: applications[0],
    isKycV2,
  }
}
